import * as React from "react";

import { createGlobalStyle, ThemeProvider } from "../styled";
import customTheme from "../styled/theme";
import { ToastProvider } from "../design-system/components/Toaster";
import { ErrorModalProvider } from "./ErrorModal";
import { IntercomProvider } from "./IntercomProvider";

const ResetStyles = createGlobalStyle`
  * {
    padding: 0;
    margin: 0;
    box-sizing: border-box;
  }

  html {
    overflow-x: hidden;
  }

  body {
    font-size: 16px;
    font-family: ${p => p.theme.typography.bodyFamily};
    letter-spacing: 0;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  button, input[type="submit"], input[type="reset"] {
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    outline: inherit;
  }

  /* Reset heading sizes by default */
  h1, h2, h3, h4, h5, h6 {
    font-size: 1rem;
  }

  /* Reset for fieldset browser styles: https://thatemil.com/blog/2015/01/03/reset-your-fieldset/ */
  fieldset {
    border: 0;
    padding: 0.01em 0 0 0;
    margin: 0;
    min-width: 0;
  }
  body:not(:-moz-handler-blocked) fieldset {
    display: table-cell;
  }

  /*
  * Pikaday
  * Copyright © 2014 David Bushell | BSD & MIT license | https://dbushell.com/
  */
  .pika-single {
    z-index: 9999;
    display: block;
    position: relative;
    color: ${p => p.theme.color.gray70};
    font-size: ${p => p.theme.typography.size.m};
    font-family: ${p => p.theme.typography.bodyFamily};
    border-radius: 5px;
  }

  /*
  clear child float (pika-lendar), using the famous micro clearfix hack
  http://nicolasgallagher.com/micro-clearfix-hack/
  */
  .pika-single:before,
  .pika-single:after {
    content: " ";
    display: table;
  }
  .pika-single:after {
    clear: both;
  }

  .pika-single.is-hidden {
    display: none;
  }

  .pika-single.is-bound {
    position: absolute;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.08);
  }

  .pika-lendar {
    width: 300px;
    background-color: ${p => p.theme.color.gray10};
    overflow: hidden;
  }

  .pika-title {
    position: relative;
    text-align: center;
  }

  .pika-label {
    display: inline-block;
    position: relative;
    z-index: 9999;
    overflow: hidden;
    margin: 0 2px;
    height: 50px;
    font-weight: ${p => p.theme.typography.weight.bold};
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }
  .pika-title select {
    cursor: pointer;
    position: absolute;
    z-index: 9998;
    margin: 0;
    left: 0;
    top: 0;
    opacity: 0;
    height: 100%;
  }

  .pika-prev,
  .pika-next {
    display: block;
    cursor: pointer;
    position: relative;
    outline: none;
    border: 0;
    padding: 0;
    width: 40px;
    height: 50px;
    /* hide text using text-indent trick, using width value (it's enough) */
    text-indent: 100px;
    white-space: nowrap;
    overflow: hidden;
    background-color: transparent;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: 16px;;
    opacity: 0.6;
  }

  .pika-prev:hover,
  .pika-next:hover {
    opacity: 1;
  }

  .pika-prev,
  .is-rtl .pika-next {
    float: left;
    background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M17.319 20a4.404 4.404 0 011.32-3.148L34.893.836a3.044 3.044 0 014.225.033 2.937 2.937 0 01.034 4.163l-14.888 14.67a.416.416 0 000 .595l14.888 14.67a2.937 2.937 0 01-.034 4.164 3.044 3.044 0 01-4.225.033L18.64 23.148A4.404 4.404 0 0117.319 20z' fill='%23445256'/><path d='M0 20a4.404 4.404 0 011.32-3.148L17.574.836A3.044 3.044 0 0121.8.869a2.937 2.937 0 01.034 4.163L6.946 19.702a.416.416 0 000 .595l14.887 14.67a2.937 2.937 0 01-.034 4.164 3.044 3.044 0 01-4.225.033L1.32 23.148A4.404 4.404 0 010 20z' fill='%23445256'/></svg>");
  }

  .pika-next,
  .is-rtl .pika-prev {
    float: right;
    background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M22.681 20a4.404 4.404 0 00-1.32-3.148L5.107.836A3.044 3.044 0 00.882.869a2.937 2.937 0 00-.034 4.163l14.888 14.67a.417.417 0 010 .595L.848 34.967a2.937 2.937 0 00.034 4.164 3.044 3.044 0 004.225.033L21.36 23.148A4.404 4.404 0 0022.68 20z' fill='%23445256'/><path d='M40 20a4.404 4.404 0 00-1.32-3.148L22.426.836A3.044 3.044 0 0018.2.869a2.937 2.937 0 00-.034 4.163l14.887 14.67a.416.416 0 010 .595l-14.887 14.67a2.937 2.937 0 00.034 4.164 3.044 3.044 0 004.225.033L38.68 23.148A4.404 4.404 0 0040 20z' fill='%23445256'/></svg>");
  }

  .pika-prev.is-disabled,
  .pika-next.is-disabled {
    cursor: default;
    opacity: 0;
  }

  .pika-select {
    display: inline-block;
  }

  .pika-table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    border: 0;
    background: ${p => p.theme.color.white};
    border-radius: 0 0 3px 3px;
  }

  .pika-table th,
  .pika-table td {
    width: 14.285714285714286%;
    padding: 0 0 5px 0;
    text-align: center;
  }

  .pika-table th {
    color: ${p => p.theme.color.gray70};
    font-size: ${p => p.theme.typography.size.m};
    font-weight: ${p => p.theme.typography.weight.bold};
    padding-top: 9px;
  }

  .pika-button {
    cursor: pointer;
    display: block;
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    outline: none;
    border: 0;
    width: 32px;
    height: 32px;
    color: ${p => p.theme.color.gray70};
    font-size: inherit;
    line-height: 20px;
    text-align: center;

    border-radius: 50%;
    display: inline-block;
    padding-left: 1px;
  }

  .pika-week {
    font-size: 11px;
    color: #999;
  }

  .is-today .pika-button {
    color: ${p => p.theme.color.gray90};
  }

  .pika-button:hover,
  .pika-row.pick-whole-week:hover .pika-button {
    color: ${p => p.theme.color.blue70};
    background: ${p => p.theme.color.blue10};
    box-shadow: none;
  }

  .is-selected .pika-button,
  .has-event .pika-button {
    color: ${p => p.theme.color.gray90};
    background: ${p => p.theme.color.green40};
  }

  .has-event .pika-button {
    background: #005da9;
  }

  .is-disabled .pika-button,
  .is-inrange .pika-button {
    background: ${p => p.theme.color.white};
  }

  .is-startrange .pika-button {
    color: #fff;
    background: #6cb31d;
  }

  .is-endrange .pika-button {
    color: #fff;
    background: #33aaff;
  }

  .is-disabled .pika-button {
    pointer-events: none;
    cursor: not-allowed;
    color: ${p => p.theme.color.gray40};
  }

  .is-outside-current-month .pika-button {
    color: ${p => p.theme.color.gray40};
  }

  .is-selection-disabled {
    pointer-events: none;
    cursor: default;
  }

  /* styling for abbr */
  .pika-table abbr {
    text-decoration: none;
    border-bottom: none;
    cursor: help;
  }
`;

interface AppWrapperProps {
  children: React.ReactNode;
}

const AppWrapper = (props: AppWrapperProps) => (
  <ThemeProvider theme={customTheme}>
    <ToastProvider>
      <ErrorModalProvider>
        <IntercomProvider>
          <>
            <ResetStyles />
            {props.children}
          </>
        </IntercomProvider>
      </ErrorModalProvider>
    </ToastProvider>
  </ThemeProvider>
);

export default AppWrapper;
